import { MeshBuilder, type Mesh } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import { click, fm, later, mallet, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { reload } from "../audio-cues.ts";
import type { ClientHero } from "../types.ts";

const V = { fuel: 0, barrage: 1, lockTarget: 2, legend: 3 } as const;
const FX = { Blaster: fxk(HeroId.StarLorde, 0), Barrage: fxk(HeroId.StarLorde, 1), Lock: fxk(HeroId.StarLorde, 2) } as const;

const theme = {
  bpm: 150,
  octave: 1,
  voice: mallet,
  notes: [
    [0, 0.25],
    [4, 0.25],
    [7, 0.5],
    [12, 0.25],
    [11, 0.25],
    [7, 0.75],
  ],
} satisfies ClientHero["audio"]["theme"];

const mixtapeShot: Cue = trim(16.2, (o) => {
  click(o, 0.22, 4800, 0.003);
  tone(o, { type: "square", f: 1180, f1: 520, glide: 0.045, gain: 0.08, d: 0.055, lp: 2800 });
  noise(o, { color: "white", type: "bandpass", f: 2600, q: 1.2, gain: 0.1, d: 0.045 });
  return 0.14;
});
const encoreDodge: Cue = trim(12.8, (o) => {
  whoosh(o, 0.6, 1800, 450, 0.24);
  click(later(o, 0.07), 0.22, 2200, 0.006);
  fm(o, { f: 480, ratio: 1.6, index: 2.8, index1: 0, gain: 0.09, d: 0.2 });
  return 0.38;
});
const bootlegBoost: Cue = trim(6.7, (o) => {
  thump(o, 0.42, 110, 55, 0.14);
  noise(o, { color: "pink", type: "bandpass", f: 650, f1: 2600, gain: 0.55, a: 0.02, d: 0.35 });
  return 0.48;
});
const turntable: Cue = trim(14, (o) => {
  for (let i = 0; i < 8; i++) {
    click(later(o, i * 0.04), 0.12, 4200, 0.002);
    tone(later(o, i * 0.04), { type: "square", f: 900 + i * 35, gain: 0.045, d: 0.045, lp: 2400 });
  }
  whoosh(o, 0.35, 500, 2400, 0.42);
  return 0.72;
});
const headline: Cue = trim(5.8, (o) => {
  tone(o, { type: "sawtooth", f: 210, f1: 1200, glide: 0.24, gain: 0.1, d: 0.35, lp: 2600 });
  [0, 7, 12].forEach((s, i) => mallet(later(o, 0.18 + i * 0.055), 293.66 * 2 ** ((s + 12) / 12), 0.12, 0.65));
  noise(o, { color: "white", type: "highpass", f: 4200, gain: 0.12, a: 0.05, d: 0.35 });
  return 0.72;
});

type Gear = { gunL: Mesh; gunR: Mesh; jets: Mesh[]; lock: Mesh; coat: Mesh; eyes: Mesh[] };

const hero: ClientHero<Gear> = {
  id: HeroId.StarLorde,
  bulk: 1,
  shape: { head: 1.03, legs: 1.06 },
  gear(g) {
    const { scene, rig, add, addWorld, glow, toon, dark } = g;
    const mask = add(MeshBuilder.CreateBox("rogue-mask", { width: 0.56, height: 0.36, depth: 0.06 }, scene), rig.head, dark);
    mask.position.set(0, 0.02, -0.29);
    const eyes: Mesh[] = [];
    for (const side of [-1, 1]) {
      const eye = add(MeshBuilder.CreateBox("red-visor-eye", { width: 0.13, height: 0.07, depth: 0.035 }, scene), rig.head, glow("#ff3030", 0.85));
      eye.position.set(side * 0.14, 0.07, -0.33);
      eyes.push(eye);
    }
    const mkGun = (parent: typeof rig.handL, name: string, side: number) => {
      const gun = add(MeshBuilder.CreateBox(name, { width: 0.16, height: 0.2, depth: 0.72 }, scene), parent, dark);
      gun.position.z = -0.28;
      gun.rotation.z = side * 0.08;
      const barrel = add(MeshBuilder.CreateCylinder(`${name}-barrel`, { diameter: 0.08, height: 0.34, tessellation: 8 }, scene), parent, toon("#9aa2b2"));
      barrel.rotation.x = Math.PI / 2;
      barrel.position.z = -0.72;
      const muzzle = add(MeshBuilder.CreateCylinder(`${name}-muzzle`, { diameter: 0.12, height: 0.1, tessellation: 8 }, scene), parent, glow("#f6d36a"));
      muzzle.rotation.x = Math.PI / 2;
      muzzle.position.z = -0.92;
      return gun;
    };
    const coat = add(MeshBuilder.CreateBox("long-space-coat", { width: 0.95, height: 1.35, depth: 0.06 }, scene), rig.torso, toon("#7a2d2d"));
    coat.position.set(0, -0.26, 0.42);
    coat.rotation.x = 0.12;
    const pack = add(MeshBuilder.CreateBox("jet-pack", { width: 0.55, height: 0.7, depth: 0.18 }, scene), rig.torso, toon("#263044"));
    pack.position.set(0, 0.02, 0.48);
    const jets: Mesh[] = [];
    for (const side of [-1, 1]) {
      const j = add(MeshBuilder.CreateCylinder("boot-jet", { diameterTop: 0.025, diameterBottom: 0.2, height: 0.34, tessellation: 10 }, scene), side < 0 ? rig.footL : rig.footR, glow("#d9722b", 0.72));
      j.rotation.x = Math.PI;
      j.position.y = -0.18;
      jets.push(j);
    }
    const lock = addWorld(MeshBuilder.CreateTorus("lock-ring", { diameter: 1.1, thickness: 0.035, tessellation: 36 }, scene), glow("#fff06a", 0.8));
    lock.setEnabled(false);
    return { gunL: mkGun(rig.handL, "left-blaster", -1), gunR: mkGun(rig.handR, "right-blaster", 1), jets, lock, coat, eyes };
  },
  animate(gear, f) {
    const flying = f.moveKind === 8 || f.v[V.legend] > 0;
    for (const j of gear.jets) {
      j.setEnabled(flying);
      if (flying) j.scaling.set(1 + Math.sin(f.t * 35) * 0.1, 1 + Math.sin(f.t * 27) * 0.18, 1);
    }
    gear.coat.rotation.x = 0.12 + (flying ? 0.25 : 0) + Math.sin(f.t * 3) * 0.03;
    if (f.v[V.barrage] > 0) {
      gear.gunL.rotation.z = f.t * 12;
      gear.gunR.rotation.z = -f.t * 12;
    } else {
      gear.gunL.rotation.z = -0.08;
      gear.gunR.rotation.z = 0.08;
    }
    for (const eye of gear.eyes) eye.scaling.setAll(f.v[V.legend] > 0 ? 1.35 + Math.sin(f.t * 20) * 0.08 : 1);
    const target = f.v[V.lockTarget] > 0 ? f.lookup(f.v[V.lockTarget]) : undefined;
    gear.lock.setEnabled(!!target);
    if (target) {
      gear.lock.position.copyFromFloats(target.center.x, target.center.y, target.center.z);
      gear.lock.rotation.y = f.t * 3;
      gear.lock.rotation.z = f.t * 5;
    }
    if (f.action === 1 || f.v[V.barrage] > 0 || f.v[V.legend] > 0) return "holding-both-shoot";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Jet fuel", value: hv(c, eid, V.fuel) / 1800, color: "#d9722b" },
      { label: "Barrage", value: hv(c, eid, V.barrage) / 180, color: "#f6d36a" },
      { label: "Legend", value: hv(c, eid, V.legend) / 480, color: "#fff06a" },
    ];
  },
  lifting(c, eid, buttons) {
    return !!(buttons & Btn.Ability1) && hv(c, eid, V.fuel) > 0;
  },
  fx: {
    [FX.Blaster]: {
      tracer(c, from, to) {
        const ray = MeshBuilder.CreateCylinder("star-blaster-ray", { diameter: 0.045, height: 1, tessellation: 8 }, c.scene);
        const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
        const len = Math.hypot(dx, dy, dz);
        ray.position.set(from.x + dx * 0.5, from.y + dy * 0.5, from.z + dz * 0.5);
        ray.scaling.set(1, len, 1);
        ray.rotation.y = Math.atan2(dx, dz);
        ray.rotation.x = Math.PI / 2 + Math.asin(dy / (len || 1));
        ray.material = c.glow("#f6d36a", 0.65);
        c.timed(ray, 0.08, (t, m) => m.scaling.set(1 - t * 0.5, len, 1 - t * 0.5));
      },
    },
    [FX.Lock]: { keepGeneric: true },
    [FX.Barrage]: { keepGeneric: true },
  },
  icons: {
    [Slot.Primary]: "M6 16l4-8M14 16l4-8M8 14h3M16 14h3",
    [Slot.Secondary]: "M5 12c4-6 10-6 14 0M12 6v12",
    [Slot.Ability1]: "M12 3l4 8h-3l2 10-7-12h3z",
    [Slot.Ability2]: "M12 5a7 7 0 110 14 7 7 0 010-14M12 8v8M8 12h8",
    [Slot.Ultimate]: "M4 12a8 8 0 0116 0M8 12h8M12 8v8",
    passive: "M6 6h12M8 18h8M7 6l2 12M17 6l-2 12",
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:star-lorde:mixtape", cue: mixtapeShot }),
    reload: (seconds) => reload(HeroId.CaptainAmericano, seconds),
    ability: {
      [Slot.Secondary]: encoreDodge,
      [Slot.Ability1]: bootlegBoost,
      [Slot.Ability2]: turntable,
      [Slot.Ultimate]: headline,
    },
    theme,
    themeTrim: { heroic: 1.5, hostile: -6.1, tease: 4 },
    loops: ({ v, ultActive, alive }) => {
      const out = [];
      if (alive && v[V.barrage] > 0) out.push({ key: "barrage", build: loops.barrage, gain: 0.75 });
      if (alive && ultActive) out.push({ key: "legend", build: loops.visor, gain: 0.7 });
      return out;
    },
    selfLift: ({ alive }) => (alive ? loops.jets : undefined),
  },
};

export default hero;
